"use client"
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ThemeInterface } from "@/interfaces/contextInterfaces/ThemeInterface";

// Brighter Light Theme
const lightTheme = {
  primary: "#7c3aed", // violet-600
  secondary: "#a78bfa", // violet-300
  accent: "#f472b6", // pink-400
  background: {
    main: "#f8fafc", // slate-50
    light: "#ffffff",
    dark: "#eef2ff", // indigo-50
  },
  text: {
    primary: "#0f172a", // slate-900
    secondary: "#334155", // slate-700
    muted: "#64748b", // slate-500
  },
  border: "#e9d5ff", // violet-200
  shadow:
    "0 10px 15px -3px rgba(124,58,237,0.12), 0 4px 6px -4px rgba(124,58,237,0.08)",
  dropShadow:
    "drop-shadow(0 8px 10px rgb(124 58 237 / 0.10)) drop-shadow(0 3px 5px rgb(124 58 237 / 0.06))",
  backdropFilter: "blur(10px)",
};

// Dark Theme
const darkTheme = {
  primary: "#a78bfa", // violet-300
  secondary: "#7c3aed", // violet-600
  accent: "#fb7185", // rose-400
  background: {
    main: "#0b1020", // deep navy
    light: "#111827", // gray-900
    dark: "#0f172a", // slate-900
  },
  text: {
    primary: "#f8fafc", // slate-50
    secondary: "#cbd5e1", // slate-300
    muted: "#94a3b8", // slate-400
  },
  border: "#312e81", // indigo-900
  shadow:
    "0 10px 15px -3px rgba(167,139,250,0.18), 0 4px 6px -4px rgba(167,139,250,0.12)",
  dropShadow:
    "drop-shadow(0 8px 10px rgb(167 139 250 / 0.15)) drop-shadow(0 3px 5px rgb(167 139 250 / 0.10))",
  backdropFilter: "blur(10px)",
};

type ThemeMode = "light" | "dark";

const ThemeContext = createContext<ThemeInterface>({
  colors: lightTheme,
  mode: "light",
  setMode: () => {},
  toggleMode: () => {},
});

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>("dark");

  useEffect(() => {
    try {
      const saved = window.localStorage.getItem("theme-mode");
      if (saved === "light" || saved === "dark") {
        setMode(saved);
      }
    } catch {}
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem("theme-mode", mode);
    } catch {}
  }, [mode]);

  const value = useMemo<ThemeInterface>(() => ({
    colors: mode === "dark" ? darkTheme : lightTheme,
    mode,
    setMode: (m: ThemeMode) => setMode(m),
    toggleMode: () => setMode((m) => (m === "light" ? "dark" : "light")),
  }), [mode]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  return useContext(ThemeContext);
}